import { useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import dayjs from 'dayjs'
import { core } from '@musinsa/pbo-core'
import { PboScope } from '@/components/pbo/PboScope'
import { notice } from '@/components/pbo/notice'
import { apiErrorText } from '@/components/pbo/apiErrorText'
import { getBoardFileUrl } from '@/services/board/apis'
import type { BoardComment } from '@/services/board/types'
import { useCreateBoardComment, useDeleteBoardComment, useDeleteBoardPost, useGetBoardPost } from '@/services/board/useBoardQueries'
import { BoardContent } from './components/BoardContent'
import { DATE_FORMAT } from '@/utils/consts'
import { BOARD_BASE_PATH, COMMENT_MAX_LENGTH } from './consts'

const formatAt = (v: string | null | undefined): string => (v ? dayjs(v).format(`${DATE_FORMAT.date} ${DATE_FORMAT.time}`) : '')

/** 되돌릴 수 없는 삭제 — 확인창(MOMS-SR-02·CR-05). */
const confirmDelete = (content: string): Promise<boolean> =>
  new Promise((resolve) => {
    core.Modal.confirm({
      title: '삭제',
      content,
      okText: '삭제',
      okButtonProps: { danger: true },
      cancelText: '취소',
      onOk: () => resolve(true),
      onCancel: () => resolve(false),
    })
  })

/**
 * presign URL(5분)은 누를 때마다 받는다. 응답을 기다린 뒤 창을 열면 팝업 차단에 걸려 클릭 순간 빈 창을 먼저 연다.
 * opener 는 끊는다(noopener 를 window.open 에 주면 창 핸들이 null 이라 직접 끊는다).
 */
const openFile = async (postId: number, index: number) => {
  const win = window.open('', '_blank')
  // 팝업이 막히면 presign(감사 EXPORT_FILE 기록)을 받지 않고 안내만 한다.
  if (!win) {
    notice.invalid('팝업이 차단되어 첨부파일을 열 수 없습니다. 브라우저에서 팝업을 허용해 주세요.')
    return
  }
  win.opener = null
  try {
    const { url } = (await getBoardFileUrl(postId, index)).data
    win.location.href = url
  } catch (e) {
    win.close()
    notice.failure(apiErrorText(e, '첨부파일을 열 수 없습니다. 다시 시도해 주세요.'))
  }
}

function CommentItem({ comment, onDelete }: Readonly<{ comment: BoardComment; onDelete: (c: BoardComment) => void }>) {
  return (
    <core.Flex vertical gap={4} style={{ padding: '12px 0', borderBottom: '1px solid var(--pbo-color-border-secondary, #f0f0f0)' }}>
      <core.Flex justify="space-between" align="center">
        <core.Typography.Text type="secondary">
          {comment.writer.name || '-'} · {comment.shipperCode} · {formatAt(comment.insertedAt)}
        </core.Typography.Text>
        {comment.deletable && (
          <core.Button size="small" type="link" danger onClick={() => onDelete(comment)}>
            삭제
          </core.Button>
        )}
      </core.Flex>
      {/* 댓글은 텍스트로 그린다(HTML 아님). */}
      <core.Typography.Text style={{ whiteSpace: 'pre-wrap' }}>{comment.comment}</core.Typography.Text>
    </core.Flex>
  )
}

function BoardDetail({ postId }: Readonly<{ postId: number }>) {
  const navigate = useNavigate()
  const { data: post, isLoading, isError } = useGetBoardPost(postId)
  const deletePost = useDeleteBoardPost(postId)
  const createComment = useCreateBoardComment(postId)
  const deleteComment = useDeleteBoardComment(postId)
  const [comment, setComment] = useState('')

  if (isLoading) return <core.Flex justify="center" style={{ padding: 48 }}>불러오는 중…</core.Flex>
  if (isError || !post) {
    return (
      <core.Flex vertical align="center" gap={16} style={{ padding: 48 }}>
        <core.Empty description="게시글을 찾을 수 없습니다." />
        <core.Button onClick={() => navigate(BOARD_BASE_PATH)}>목록</core.Button>
      </core.Flex>
    )
  }

  const onDeletePost = async () => {
    if (!(await confirmDelete('게시글과 댓글을 삭제합니다. 삭제한 글은 되돌릴 수 없습니다.'))) return
    try {
      await deletePost.mutateAsync()
      notice.success('삭제했습니다.')
      navigate(BOARD_BASE_PATH)
    } catch (e) {
      notice.failure(apiErrorText(e, '삭제하지 못했습니다. 다시 시도해 주세요.'))
    }
  }

  const onSubmitComment = async () => {
    const text = comment.trim()
    if (!text) {
      notice.invalid('내용을 입력해 주세요.')
      return
    }
    try {
      await createComment.mutateAsync(text)
      setComment('')
      notice.success('댓글을 등록했습니다.')
    } catch (e) {
      notice.failure(apiErrorText(e, '댓글을 등록하지 못했습니다. 다시 시도해 주세요.'))
    }
  }

  const onDeleteComment = async (c: BoardComment) => {
    if (!(await confirmDelete('댓글을 삭제합니다. 삭제한 댓글은 되돌릴 수 없습니다.'))) return
    try {
      await deleteComment.mutateAsync(c.id)
      notice.success('삭제했습니다.')
    } catch (e) {
      notice.failure(apiErrorText(e, '삭제하지 못했습니다. 다시 시도해 주세요.'))
    }
  }

  return (
    <core.Flex vertical gap={16}>
      <core.Flex justify="space-between" align="center">
        <core.Typography.Title level={4} style={{ margin: 0 }}>
          게시판
        </core.Typography.Title>
        <core.Space>
          <core.Button onClick={() => navigate(BOARD_BASE_PATH)}>목록</core.Button>
          {post.editable && <core.Button onClick={() => navigate(`${BOARD_BASE_PATH}/${post.id}/edit`)}>수정</core.Button>}
          {post.deletable && (
            <core.Button danger loading={deletePost.isPending} onClick={onDeletePost}>
              삭제
            </core.Button>
          )}
        </core.Space>
      </core.Flex>

      <core.Card>
        <core.Flex vertical gap={8}>
          <core.Flex gap={8} align="center">
            {post.notice && <core.Tag color="processing">공지</core.Tag>}
            <core.Typography.Text type="secondary">{post.category}</core.Typography.Text>
          </core.Flex>
          <core.Typography.Title level={5} style={{ margin: 0 }}>
            {post.title}
          </core.Typography.Title>
          <core.Typography.Text type="secondary">
            {post.writer.name || '-'} · {post.shipperCode} · {formatAt(post.insertedAt)} · 조회 {post.viewCount}
          </core.Typography.Text>
          <core.Divider style={{ margin: '8px 0' }} />
          <BoardContent html={post.content} />
          {post.files.length > 0 && (
            <>
              <core.Divider style={{ margin: '8px 0' }} />
              <core.Flex vertical gap={4} align="flex-start">
                <core.Typography.Text strong>첨부파일 {post.files.length}</core.Typography.Text>
                {post.files.map((f) => (
                  <core.Button key={f.index} type="link" size="small" style={{ padding: 0 }} onClick={() => openFile(post.id, f.index)}>
                    {f.fileName}
                  </core.Button>
                ))}
              </core.Flex>
            </>
          )}
        </core.Flex>
      </core.Card>

      <core.Card title={`댓글 ${post.comments.length}`}>
        {post.comments.map((c) => (
          <CommentItem key={c.id} comment={c} onDelete={onDeleteComment} />
        ))}
        <core.Flex vertical gap={8} style={{ marginTop: 12 }}>
          <core.Input.TextArea
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            maxLength={COMMENT_MAX_LENGTH}
            showCount
            autoSize={{ minRows: 3, maxRows: 8 }}
            placeholder="댓글을 입력하세요"
          />
          <core.Flex justify="flex-end">
            <core.Button type="primary" loading={createComment.isPending} onClick={onSubmitComment}>
              댓글 등록
            </core.Button>
          </core.Flex>
        </core.Flex>
      </core.Card>
    </core.Flex>
  )
}

export default function BoardDetailPage() {
  const { id } = useParams()
  return (
    <PboScope>
      <BoardDetail postId={Number(id)} />
    </PboScope>
  )
}
